// The player's look: skin tone, eye colour, hairstyle, hair colour (character look, 2026-10-29). The ids and
// colours live in backend/app/game/look_catalog.json; this constant must stay deep-equal to that file (pinned by
// lookCatalog.test.mjs). A missing look, or a missing / unknown field, means the body's default — every player from
// before this feature looks exactly as before.
export const LOOK_CATALOG = {
  "skin": [
    {
      "id": "s1",
      "shadow": "#e8b9a0",
      "mid": "#fbe3d3",
      "light": "#fff1e8"
    },
    {
      "id": "s2",
      "shadow": "#f0b388",
      "mid": "#fcd5ae",
      "light": "#ffe6c8"
    },
    {
      "id": "s3",
      "shadow": "#d29a6e",
      "mid": "#e9bb8f",
      "light": "#f6d2ad"
    },
    {
      "id": "s4",
      "shadow": "#b08270",
      "mid": "#d9a385",
      "light": "#e8b994"
    },
    {
      "id": "s5",
      "shadow": "#8f6350",
      "mid": "#b98668",
      "light": "#ce9e7d"
    },
    {
      "id": "s6",
      "shadow": "#6e4632",
      "mid": "#9a6a4c",
      "light": "#b48366"
    },
    {
      "id": "s7",
      "shadow": "#3d261c",
      "mid": "#5f3d2c",
      "light": "#7a5240"
    }
  ],
  "eyes": [
    {
      "id": "dark_brown",
      "dark": "#2e1c12",
      "light": "#5a3a24"
    },
    {
      "id": "brown",
      "dark": "#4a2c18",
      "light": "#8a5a34"
    },
    {
      "id": "hazel",
      "dark": "#5a4220",
      "light": "#a8873e"
    },
    {
      "id": "green",
      "dark": "#2f5a2a",
      "light": "#6f9a52"
    },
    {
      "id": "blue",
      "dark": "#23476e",
      "light": "#5f95c8"
    },
    {
      "id": "grey",
      "dark": "#4a5560",
      "light": "#9aa6b0"
    }
  ],
  "hairColor": [
    {
      "id": "black",
      "shadow": "#141012",
      "mid": "#2a2226",
      "light": "#4a3e44",
      "brow": "#2a2226"
    },
    {
      "id": "dark_brown",
      "shadow": "#2e1c12",
      "mid": "#4e3020",
      "light": "#74503a",
      "brow": "#3a2618"
    },
    {
      "id": "light_brown",
      "shadow": "#6a4428",
      "mid": "#9a6a42",
      "light": "#c09068",
      "brow": "#6e4a30"
    },
    {
      "id": "blond",
      "shadow": "#e2aa52",
      "mid": "#f6c163",
      "light": "#fdd890",
      "brow": "#c89a5a"
    },
    {
      "id": "ginger",
      "shadow": "#9f4d18",
      "mid": "#bf7035",
      "light": "#d88c5a",
      "brow": "#a0582a"
    },
    {
      "id": "pink",
      "shadow": "#c4527e",
      "mid": "#ea86ab",
      "light": "#f7b6cf",
      "brow": "#4a3226"
    },
    {
      "id": "blue",
      "shadow": "#2f5fae",
      "mid": "#5a8ee0",
      "light": "#93bdf2",
      "brow": "#4a3226"
    },
    {
      "id": "purple",
      "shadow": "#6a3a9a",
      "mid": "#9a66cc",
      "light": "#c49ae6",
      "brow": "#4a3226"
    }
  ],
  "hairstyles": {
    "natalka": [
      {
        "id": "nat_pigtails"
      },
      {
        "id": "nat_long_sleek"
      },
      {
        "id": "nat_rounded_bob"
      },
      {
        "id": "nat_wavy_lob"
      },
      {
        "id": "nat_half_up"
      },
      {
        "id": "nat_ringlets"
      },
      {
        "id": "nat_ponytail"
      }
    ],
    "ken": [
      {
        "id": "ken_curls"
      },
      {
        "id": "ken_short_crop"
      },
      {
        "id": "ken_spiky"
      },
      {
        "id": "ken_buzz"
      },
      {
        "id": "ken_fringe"
      }
    ]
  },
  "defaults": {
    "natalka": {
      "skin": "s2",
      "eyes": "brown",
      "hairstyle": "nat_pigtails",
      "hairColor": "blond"
    },
    "ken": {
      "skin": "s2",
      "eyes": "green",
      "hairstyle": "ken_curls",
      "hairColor": "ginger"
    }
  }
};

export const LOOK_FIELDS = ['skin', 'eyes', 'hairstyle', 'hairColor'];

const known = (field, character, id) => (field === 'hairstyle'
  ? (LOOK_CATALOG.hairstyles[character] || []).some((h) => h.id === id)
  : LOOK_CATALOG[field].some((e) => e.id === id));

const bodyOf = (character) => (LOOK_CATALOG.defaults[character] ? character : 'natalka');

/** The full look {skin, eyes, hairstyle, hairColor}: the player's picks, the body's defaults for the rest. */
export function resolveLook(character, look) {
  const body = bodyOf(character);
  const out = { ...LOOK_CATALOG.defaults[body] };
  for (const f of LOOK_FIELDS) if (look && known(f, body, look[f])) out[f] = look[f];
  return out;
}

/** True when the look draws exactly like the body's own art (nothing to recolour). */
export function isDefaultLook(character, look) {
  const r = resolveLook(character, look), d = LOOK_CATALOG.defaults[bodyOf(character)];
  return LOOK_FIELDS.every((f) => r[f] === d[f]);
}
